import { useState } from "react";
import { Button } from "../../components/ui/button";
import { IndianRupee, FlaskConical } from "lucide-react";
import { initialFlats, initialConfirmQueue } from "./mockData";
import { TreasurerDashboard } from "./TreasurerDashboard";
import { FlatsBoard } from "./FlatsBoard";
import { RemindersTab } from "./RemindersTab";
import { SetupTab } from "./SetupTab";
import { ResidentView } from "./ResidentView";
import { RecordPaymentDialog } from "./RecordPaymentDialog";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "board", label: "Flats board" },
  { id: "reminders", label: "Reminders" },
  { id: "setup", label: "Setup" },
];

export default function DuesPreviewPage() {
  const [viewAs, setViewAs] = useState("treasurer");
  const [tab, setTab] = useState("overview");
  const [flats, setFlats] = useState(initialFlats);
  const [queue, setQueue] = useState(initialConfirmQueue);
  const [recordFor, setRecordFor] = useState(null);
  const [recordOpen, setRecordOpen] = useState(false);

  function markPaid(flatId) {
    setFlats((fs) => fs.map((f) => f.id === flatId
      ? { ...f, status: "paid", dueNow: 0, arrears: 0, lastPaid: "14 Jun · UPI", receipts: [`RCP-2026-0${40 + Math.floor(Math.random() * 50)} · Jun · confirmed`, ...f.receipts] }
      : f));
  }
  function confirmQueued(q) { markPaid(q.flatId); setQueue((qs) => qs.filter((x) => x.id !== q.id)); }
  function rejectQueued(q) {
    setFlats((fs) => fs.map((f) => f.id === q.flatId ? { ...f, status: "pending" } : f));
    setQueue((qs) => qs.filter((x) => x.id !== q.id));
  }
  function writeOff(flatId) {
    setFlats((fs) => fs.map((f) => f.id === flatId ? { ...f, arrears: 0, status: f.dueNow > 0 ? f.status : "paid", note: "Old arrears written off (GB decision) — logged in audit trail" } : f));
  }
  function selfReport(flatId) {
    setFlats((fs) => fs.map((f) => f.id === flatId ? { ...f, status: "awaiting", note: "Self-reported payment — waiting for treasurer confirmation" } : f));
    setQueue((qs) => [...qs, {
      id: "pc" + Date.now(), flatId, flat: "A-103", name: "Suresh Babu", source: "Screenshot uploaded by resident",
      parsed: { amount: 10000, date: "14 Jun 2026", utr: "512099883321", payer: "SURESH B", app: "PhonePe" },
      allocation: "Apr + May + Jun 2026 + part arrears (oldest first)",
    }]);
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 md:py-8" data-testid="dues-preview-page">
      <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-2.5 mb-5 flex items-center gap-2 text-xs text-amber-800" data-testid="mockup-banner">
        <FlaskConical size={15} className="shrink-0" />
        <span><b>Mockup for discussion</b> — sample data only, nothing is saved. Tap around freely; refresh to reset.</span>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl md:text-3xl font-semibold flex items-center gap-2">
            <IndianRupee className="text-[#C05640]" size={26} /> Maintenance & Dues
          </h1>
          <p className="text-sm text-stone-500 mt-1">Track collections without Excel, screenshots-hunting or awkward reminders.</p>
        </div>
        <div className="inline-flex rounded-full border border-stone-200 bg-white p-1 text-xs font-medium">
          <button onClick={() => setViewAs("treasurer")} data-testid="viewas-treasurer"
            className={`px-3.5 py-1.5 rounded-full transition ${viewAs === "treasurer" ? "bg-stone-900 text-white" : "text-stone-500"}`}>Treasurer</button>
          <button onClick={() => setViewAs("resident")} data-testid="viewas-resident"
            className={`px-3.5 py-1.5 rounded-full transition ${viewAs === "resident" ? "bg-stone-900 text-white" : "text-stone-500"}`}>Resident</button>
        </div>
      </div>

      {viewAs === "treasurer" ? (
        <>
          <div className="mt-5 mb-5 flex gap-1.5 overflow-x-auto pb-1">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dues-tab-${t.id}`}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition ${tab === t.id ? "bg-[#F4F1EA] text-[#C05640] font-semibold" : "text-stone-500 hover:bg-stone-50"}`}>
                {t.label}
                {t.id === "overview" && queue.length > 0 && <span className="ml-1.5 text-[10px] bg-sky-500 text-white px-1.5 py-0.5 rounded-full">{queue.length}</span>}
              </button>
            ))}
          </div>

          {tab === "overview" && <TreasurerDashboard flats={flats} queue={queue} onConfirm={confirmQueued} onReject={rejectQueued} />}
          {tab === "board" && <FlatsBoard flats={flats} onRecordFor={(id) => { setRecordFor(id); setRecordOpen(true); }} onWriteOff={writeOff} />}
          {tab === "reminders" && <RemindersTab flats={flats} />}
          {tab === "setup" && <SetupTab flats={flats} />}

          {(tab === "overview" || tab === "board") && (
            <Button onClick={() => { setRecordFor(null); setRecordOpen(true); }} data-testid="fab-record-payment"
              className="fixed bottom-24 md:bottom-8 right-4 md:right-8 rounded-full h-12 px-5 bg-[#C05640] hover:bg-[#A8492F] shadow-lg shadow-[#C05640]/30 z-40">
              <IndianRupee size={17} className="mr-1" /> Record payment
            </Button>
          )}
        </>
      ) : (
        <div className="mt-5">
          <ResidentView flats={flats} onSelfReport={selfReport} />
        </div>
      )}

      <RecordPaymentDialog open={recordOpen} onClose={() => setRecordOpen(false)}
        flats={flats} defaultFlatId={recordFor}
        onRecorded={(flatId) => flatId && markPaid(flatId)} />
    </div>
  );
}
